/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect, useState } from 'react';

// Lib dependencies
import {
	ActionIcon,
	Drawer,
	Flex,
	Group,
	Radio,
	Stack,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

// Styles
import classes from './Filter.module.css';

// UI local components
import { Button, Title } from '@components/v2/ui';

// Icons
import { IconX } from '@tabler/icons-react';

// Utils
import { MultiSelect } from '@components/v2/ui/MultiSelect/MultiSelect';
import { ACLS } from '@config/constants/acls';
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';
import useStore from '@store/index';
import { checkAclFront } from '@utils/acl-front';
import {
	cloneDeep,
	convertArrayToLabelValue,
} from '@utils/functions';
import isNil from 'lodash/isNil';
import { useCountryCodes } from 'shared/api/get-country-codes';
import { useOrganizationsHasProducers } from 'shared/api/get-organizations-has-entity-producer';
import { useProducers } from '../../hooks/useProducers';

/* -------------------------------------------------------------------------- */
/*                                   Types                                     */
/* -------------------------------------------------------------------------- */

export type RangeFilter = {
	$gte: number;
	$lte: number;
};

export type QueryParams = {
	'location.countryCode'?: { $in: string[] };
	organization?: string[];
	sellPrice_europertco2eq?: RangeFilter;
	'quantity.remaining_aco2'?: RangeFilter;
	[key: string]: unknown;
	verified?: boolean;
};

type TFilterOffsetsProps = {
	opened: boolean;
	onClose: () => void;
	title: string;
	onApplyFilters: (filters: QueryParams) => void;
	value: QueryParams;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const Filter = ({
	opened,
	onClose,
	title,
	onApplyFilters,
	value,
}: TFilterOffsetsProps) => {
	/* ******************************** HOOKS ******************************* */
	const { t } = useTranslation();
	const [
		valuePricePerTonCO2Slider,
		setPricePerTonCO2ValueSlider,
	] = useState<[number, number]>([0, 100]);
	const [valueAvailableStock, setValueAvailableStock] =
		useState<[number, number]>([0, 20000]);

	const { setProducersQueryParams } = useProducers();

	const [queryParams, setQueryParams] =
		useState<QueryParams>(value ?? {});

	const { data: organizations } =
		useOrganizationsHasProducers();
	const { checkRole, role } = useAuthorization();

	const { setProducersPage } = useStore();

	/* ******************************** HELPERS ******************************* */

	const handleReset = () => {
		setProducersQueryParams({});
		setPricePerTonCO2ValueSlider([0, 100]);
		setValueAvailableStock([0, 20000]);
		setQueryParams({});
		onApplyFilters({});
		onClose();
	};

	const { data: countryCodes } =
		useCountryCodes('brands');
	const filteredCountries =
		!isNil(countryCodes) && Array.isArray(countryCodes)
			? EUROPEAN_COUNTRIES.filter((item) =>
					countryCodes.includes(item.value)
				)
			: [];

	const handleApplyFilters = () => {
		setProducersQueryParams(queryParams);
		setProducersPage(1);
		onApplyFilters(queryParams);
		onClose();
	};

	useEffect(() => {
		if (opened) {
			setQueryParams(cloneDeep(value ?? {}));
		}
	}, [opened, value]);

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer
			classNames={classes}
			opened={opened}
			position="right"
			onClose={onClose}
			withCloseButton={false}
			size="544px"
		>
			{/* Header */}
			<Flex
				align="center"
				gap="16px"
				justify="space-between"
				pb={'var(--mantine-spacing-xl)'}
			>
				<Title
					order={6}
					c="var(--mantine-color-igluPrimary-9)"
				>
					{title}
				</Title>
				<ActionIcon
					color="var(--mantine-color-igluBorder-9)"
					radius={'48px'}
					variant="outline"
					size={'48px'}
					onClick={onClose}
				>
					<IconX
						color="var(--mantine-color-igluPrimary-9)"
						size={'24px'}
					/>
				</ActionIcon>
			</Flex>

			{/* Drawer content */}
			<Flex
				flex={1}
				direction={'column'}
				justify="space-between"
			>
				<Stack gap={'var(--mantine-spacing-sm)'}>
					{checkAclFront(
						ACLS.FRONT.PRODUCERS.FILTER_STATUS,
						role as ROLES
					) && (
						<Radio.Group
							label={t('global.status')}
							value={
								queryParams?.verified === true
									? 'active'
									: queryParams?.verified === false
										? 'inActive'
										: 'all'
							}
							onChange={(value) => {
								if (value === 'all') {
									const { verified, ...rest } =
										queryParams;
									setQueryParams(rest);
								} else {
									setQueryParams({
										...queryParams,
										verified: value === 'active',
									});
								}
							}}
						>
							<Group mt="xs">
								<Group mt="xs">
									<Radio
										value="all"
										label={t('global.all')}
									/>
									<Radio
										value="active"
										label={t('global.active')}
									/>
									<Radio
										value="inActive"
										label={t('global.inactive')}
									/>
								</Group>
							</Group>
						</Radio.Group>
					)}

					{checkAclFront(
						ACLS.FRONT.PRODUCERS.FILTER_COUNTRIES,
						role as ROLES
					) && (
						<MultiSelect
							placeholder={t('global.country')}
							w={'100%'}
							comboboxProps={{ withinPortal: false }}
							data={filteredCountries}
							clearable
							size="lg"
							value={
								queryParams['location.countryCode']
									? (queryParams['location.countryCode']
											?.$in ?? [])
									: []
							}
							searchable
							onChange={(value) => {
								if (value.length === 0) {
									const _cloneQueryParams =
										cloneDeep(queryParams);
									delete _cloneQueryParams[
										'location.countryCode'
									];
									setQueryParams({
										..._cloneQueryParams,
									});
									return;
								}

								setQueryParams({
									...queryParams,
									'location.countryCode': { $in: value },
								});
							}}
						/>
					)}
					{checkAclFront(
						ACLS.FRONT.PRODUCERS.FILTER_ORGANIZATIONS,
						role as ROLES
					) &&
						!checkRole([ROLES.ORGANIZATION_ADMIN]) && (
							<MultiSelect
								placeholder={t('global.organizations')}
								w={'100%'}
								comboboxProps={{ withinPortal: false }}
								data={convertArrayToLabelValue(
									organizations
								)}
								clearable
								size="lg"
								value={queryParams.organization ?? []}
								searchable
								onChange={(value) => {
									if (value.length === 0) {
										const _cloneQueryParams =
											cloneDeep(queryParams);
										delete _cloneQueryParams[
											'organization'
										];
										setQueryParams(_cloneQueryParams);
										return;
									}
									setQueryParams({
										...queryParams,
										organization: value,
									});
								}}
							/>
						)}
				</Stack>

				{/* Buttons */}
				<Flex
					direction={{
						base: 'column-reverse',
						sm: 'row',
					}}
					gap={{
						base: 'var(--mantine-spacing-sm)',
						sm: 'var(--mantine-spacing-lg)',
					}}
				>
					<Button
						fullWidth
						variant={'outline'}
						onClick={handleReset}
					>
						{t('global.resetFilters')}
					</Button>
					<Button
						onClick={handleApplyFilters}
						fullWidth
						variant={'primary'}
					>
						{t('global.applyFilters')}
					</Button>
				</Flex>
			</Flex>
		</Drawer>
	);
};
